/* global tinymce */
import React, { useRef, useEffect, useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import logo from "../../../assets/logo-dark.svg";
import { Editor } from "@tinymce/tinymce-react";
import {
  Tabs,
  Form,
  Input,
  Checkbox,
  Button,
  Row,
  Col,
  message,
  Card,
  Space,
  Typography,
  Select,
  Spin,
  Modal,
  Tag,
  Table,
  Alert,
} from "antd";
import { updateUserProfile } from "../../../redux/actions/userAction";
import {
  updateOrganization,
  fetchOrganizationDetail,
  updateDpdpSettings,
} from "../../../redux/actions/organizationAction";
import TeamSettings from "./TeamSettings";
import DeliverabilitySettings from "./DeliverabilitySettings";
import {
  canManageBilling,
  canManageEmailSettings,
  canManageOrg,
} from "../../../utils/rbac";
import { getActiveOrganizationId } from "../../../utils/organizationId";
import {
  privateGetApi,
  privatePostApi,
  privatePutApi,
  privateDeleteApi,
} from "../../../utils/apiHelper";

const { TextArea } = Input;

const TAB_QUERY_TO_KEY = {
  billing: "billing-and-payments",
  profile: "profile-settings",
  organization: "organization",
  dpdp: "dpdp-compliance",
  team: "team",
  support: "contact-support",
};

const TAB_KEY_TO_QUERY = {
  "billing-and-payments": "billing",
  "profile-settings": "profile",
  organization: "organization",
  "dpdp-compliance": "dpdp",
  team: "team",
  "contact-support": "support",
};

function tabKeyFromQuery(tab) {
  return TAB_QUERY_TO_KEY[tab] || "profile-settings";
}

export default function Settings() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [userProfileForm] = Form.useForm();
  const [organizationForm] = Form.useForm();
  const [dpdpForm] = Form.useForm();
  const [dpdpLoading, setDpdpLoading] = useState(false);
  const [dpdpSaving, setDpdpSaving] = useState(false);
  const [customiseEmailForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const editorRef = useRef(null);
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : {};
  const windowOrigin = window.location.origin;
  const [whitelabel, setWhitelable] = useState(false);
  const [activeTab, setActiveTab] = useState(() =>
    tabKeyFromQuery(searchParams.get("tab")),
  );
  const [billingUsage, setBillingUsage] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [customPlanEmails, setCustomPlanEmails] = useState(null);
  const [customPlanLoading, setCustomPlanLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [formsCheckoutLoading, setFormsCheckoutLoading] = useState(false);
  const [formsCancelLoading, setFormsCancelLoading] = useState(false);
  const [customFormSubmissions, setCustomFormSubmissions] = useState(null);
  const [formsCustomPlanLoading, setFormsCustomPlanLoading] = useState(false);
  const [contactAdminModalOpen, setContactAdminModalOpen] = useState(false);
  const [contactAdminSubject, setContactAdminSubject] = useState("");
  const [contactAdminMessage, setContactAdminMessage] = useState("");
  const [contactAdminSending, setContactAdminSending] = useState(false);
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportSending, setSupportSending] = useState(false);
  const [emailSettingsState, setEmailSettingsState] = useState(
    localStorage.getItem("emailSettings")
      ? JSON.parse(localStorage.getItem("emailSettings"))
      : null,
  );
  const [passwordOtpCountdown, setPasswordOtpCountdown] = useState(0);
  const [passwordOtpCountdownActive, setPasswordOtpCountdownActive] =
    useState(false);
  const [passwordOtpSending, setPasswordOtpSending] = useState(false);
  const [senderIdentity, setSenderIdentity] = useState(null);
  const [senderIdentityLoading, setSenderIdentityLoading] = useState(false);
  const [domainVerifyModalOpen, setDomainVerifyModalOpen] = useState(false);
  const [emailVerifyModalOpen, setEmailVerifyModalOpen] = useState(false);
  const [domainVerifyValue, setDomainVerifyValue] = useState("");
  const [emailVerifyValue, setEmailVerifyValue] = useState("");
  const [verifySubmitting, setVerifySubmitting] = useState(false);
  const [applySenderLoading, setApplySenderLoading] = useState(false);
  const [senderUpdateModalOpen, setSenderUpdateModalOpen] = useState(false);

  const workEmailDomain = (() => {
    const domainOf = (email) => {
      const at = (email || "").lastIndexOf("@");
      return at > 0 ? email.slice(at + 1).toLowerCase() : "";
    };
    // Prefer the org's own configured sender/reply-to domain over the
    // logged-in user's personal email domain, so the "verify domain" prompt
    // matches what the org actually sends from (e.g. a shared mailbox on a
    // company domain that differs from any individual member's login email).
    const orgSender = emailSettingsState?.smtpEmailAddress || "";
    if (orgSender && !orgSender.toLowerCase().endsWith("@mailinflow.net")) {
      const domain = domainOf(orgSender);
      if (domain) return domain;
    }
    const orgReplyTo = domainOf(emailSettingsState?.imapEmailAddress || "");
    if (orgReplyTo) return orgReplyTo;
    return domainOf(user.emailAddress || "");
  })();

  const getDefaultBrandedFooter = useCallback(() => {
    return (
      "<div style='max-width: 600px; margin: auto;'><div class='mceEditable'></div><div class='mceNonEditable'><p style='font-size: 10pt; color: #bfbfbf; text-align: center'>You are receiving this email because you have visited our site or opted for our newsletter. Make sure our messages get to your Inbox (and not your bulk or junk folders).</p><p style='font-size: 10pt; color: #bfbfbf; text-align: center'><a href='" +
      windowOrigin +
      "/unsubscribe/" +
      user.emailAddress +
      "/" +
      user.emailAddress +
      "'>Unsubscribe</a> | <a href='" +
      windowOrigin +
      "/privacy-policy'>Privacy Policy</a></p><p style='font-size: 10pt; color: #bfbfbf; text-align: center'>Powered by<img src='" +
      logo +
      "' style='display: block; margin-top: 5px; margin-left: auto; margin-right: auto;' height='30'/></p></div></div>"
    );
  }, [windowOrigin, user.emailAddress]);

  const getEmptyWhitelabelFooter = useCallback(() => {
    // Intentionally empty: user can create the entire footer when whitelabel is enabled.
    return "<div style='max-width: 600px; margin: auto;'><div class='mceEditable'></div></div>";
  }, []);

  // Treat the user as paid if either the stored user object or billing usage
  // (once loaded) reports a paid plan.
  const planFromUsage = billingUsage?.plan;
  const isPaidPlan = planFromUsage === "paid" || user.plan === "paid";

  const resolveSenderIdentityType = (identity) => {
    if (identity?.type === "email" || identity?.type === "domain") {
      return identity.type;
    }
    return (identity?.identity || "").includes("@") ? "email" : "domain";
  };

  const resolveSuggestedSenderEmail = useCallback(
    (identity) => {
      if (!identity?.identity) return "";
      if (identity.suggestedSenderEmail) return identity.suggestedSenderEmail;
      const identityType = resolveSenderIdentityType(identity);
      if (identityType === "email") {
        return identity.identity.trim().toLowerCase();
      }
      const accountEmail = (user.emailAddress || "").trim().toLowerCase();
      if (accountEmail.endsWith(`@${workEmailDomain}`)) return accountEmail;
      return workEmailDomain ? `hello@${workEmailDomain}` : "";
    },
    [user.emailAddress, workEmailDomain],
  );

  const senderIdentityType = resolveSenderIdentityType(senderIdentity);
  const isSenderFullyVerified =
    senderIdentityType === "email"
      ? senderIdentity?.verificationStatus === "SUCCESS" ||
        (senderIdentity?.verificationStatus == null &&
          senderIdentity?.status === "SUCCESS")
      : senderIdentity?.status === "SUCCESS";
  const suggestedSenderEmail = resolveSuggestedSenderEmail(senderIdentity);
  const canUseCustomSender = isSenderFullyVerified;
  const senderDisplayStatus =
    senderIdentityType === "email"
      ? senderIdentity?.verificationStatus ||
        senderIdentity?.status ||
        "NOT_STARTED"
      : senderIdentity?.status || "NOT_STARTED";
  const smtpFormValue =
    Form.useWatch("smtpEmailAddress", customiseEmailForm) || "";
  const usesTrialSender = smtpFormValue
    .toLowerCase()
    .endsWith("@mailinflow.net");
  const senderInputDisabled =
    !isPaidPlan || (isPaidPlan && !canUseCustomSender && !usesTrialSender);

  const startPasswordOtpCountdown = () => {
    setPasswordOtpCountdown(60);
    setPasswordOtpCountdownActive(true);
  };

  useEffect(() => {
    if (!passwordOtpCountdownActive || passwordOtpCountdown <= 0) return;
    const id = setInterval(() => {
      setPasswordOtpCountdown((prev) => {
        if (prev <= 1) {
          setPasswordOtpCountdownActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [passwordOtpCountdownActive, passwordOtpCountdown]);

  const loadBillingUsage = useCallback(async () => {
    setBillingLoading(true);
    try {
      const res = await privateGetApi("/billing/usage", {});
      if (res?.data?.result) setBillingUsage(res.data.result);
    } catch {
      setBillingUsage(null);
    } finally {
      setBillingLoading(false);
    }
  }, []);

  const loadEmailSettings = useCallback(async () => {
    try {
      const res = await privateGetApi("/auth/email-settings", {});
      const latest = res?.data?.result?.emailSettings;
      if (latest) {
        setEmailSettingsState(latest);
        localStorage.setItem("emailSettings", JSON.stringify(latest));
      }
      if (res?.data?.result?.senderIdentity) {
        setSenderIdentity(res.data.result.senderIdentity);
      }
    } catch (e) {
      const fallback = localStorage.getItem("emailSettings")
        ? JSON.parse(localStorage.getItem("emailSettings"))
        : null;
      setEmailSettingsState(fallback);
    }
  }, []);

  const applySenderFromVerification = useCallback(
    async (smtpEmailAddress, { silent = false } = {}) => {
      const resolved =
        smtpEmailAddress || resolveSuggestedSenderEmail(senderIdentity);
      if (!resolved) {
        message.error(
          "Could not determine your verified sender address. Try Check status, then try again.",
          6,
        );
        return false;
      }
      const values = customiseEmailForm.getFieldsValue();
      let effectiveFooter = emailSettingsState?.emailFooter || "";
      if (editorRef.current) {
        effectiveFooter = editorRef.current.getContent() || effectiveFooter;
      }
      if (!effectiveFooter || !effectiveFooter.trim()) {
        effectiveFooter = getDefaultBrandedFooter();
      }
      const payload = {
        smtpEmailAddress: resolved,
        imapEmailAddress: values.imapEmailAddress || user.emailAddress || "",
        clickTracking: values.clickTracking || false,
        openTracking: values.openTracking ?? true,
        emailFooter: effectiveFooter,
      };
      setApplySenderLoading(true);
      try {
        const res = await privatePutApi("/auth/email-settings", payload);
        if (!res) {
          return false;
        }
        await loadEmailSettings();
        if (!silent) {
          message.success(`Sender email address set to ${resolved}`, 6);
        }
        setSenderUpdateModalOpen(false);
        return true;
      } catch (error) {
        message.error(
          error?.response?.data?.message ||
            "Failed to update sender email address",
          6,
        );
        return false;
      } finally {
        setApplySenderLoading(false);
      }
    },
    [
      customiseEmailForm,
      user.emailAddress,
      emailSettingsState,
      loadEmailSettings,
      resolveSuggestedSenderEmail,
      senderIdentity,
      getDefaultBrandedFooter,
    ],
  );

  const handleSenderIdentityPollResult = useCallback(
    (result) => {
      const latest = result?.senderIdentity;
      if (latest) setSenderIdentity(latest);

      if (result?.senderApplied && result?.smtpEmailAddress) {
        loadEmailSettings();
        message.success(
          `Verification complete. Sender email updated to ${result.smtpEmailAddress}.`,
          8,
        );
        return;
      }

      const fullyVerified =
        latest?.type === "email"
          ? latest?.verificationStatus === "SUCCESS"
          : latest?.status === "SUCCESS";
      if (fullyVerified && (latest?.needsSenderUpdate || usesTrialSender)) {
        const suggested = resolveSuggestedSenderEmail(latest);
        if (suggested) setSenderUpdateModalOpen(true);
      }
    },
    [loadEmailSettings, resolveSuggestedSenderEmail, usesTrialSender],
  );

  const loadSenderIdentity = useCallback(
    async (silent = false) => {
      if (!silent) setSenderIdentityLoading(true);
      try {
        const res = await privateGetApi("/auth/sender-identities", {});
        handleSenderIdentityPollResult(res?.data?.result);
      } catch {
        if (!silent) {
          message.error("Unable to load sender verification status.", 4);
        }
      } finally {
        if (!silent) setSenderIdentityLoading(false);
      }
    },
    [handleSenderIdentityPollResult],
  );

  const copyDnsValue = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => message.success("Copied to clipboard", 2))
      .catch(() => message.error("Could not copy", 2));
  };

  const senderStatusTag = (status) => {
    const map = {
      SUCCESS: { color: "success", label: "Verified" },
      PENDING: { color: "processing", label: "Pending" },
      FAILED: { color: "error", label: "Failed" },
      NOT_STARTED: { color: "default", label: "Not started" },
    };
    const entry = map[status] || map.NOT_STARTED;
    return <Tag color={entry.color}>{entry.label}</Tag>;
  };

  const startDomainVerification = async () => {
    const domain = (domainVerifyValue || workEmailDomain).trim().toLowerCase();
    if (!domain) {
      message.error("Enter a domain to verify.", 4);
      return;
    }
    setVerifySubmitting(true);
    try {
      const res = await privatePostApi("/auth/sender-identities", {
        type: "domain",
        identity: domain,
      });
      if (res?.data?.result?.senderIdentity) {
        setSenderIdentity(res.data.result.senderIdentity);
      }
      message.success(res?.data?.message || "Domain verification started.", 6);
      setDomainVerifyModalOpen(false);
      loadSenderIdentity(true);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to start domain verification",
        6,
      );
    } finally {
      setVerifySubmitting(false);
    }
  };

  const startEmailVerification = async () => {
    const email = emailVerifyValue.trim().toLowerCase();
    if (!email) {
      message.error("Enter an email address to verify.", 4);
      return;
    }
    setVerifySubmitting(true);
    try {
      const res = await privatePostApi("/auth/sender-identities", {
        type: "email",
        identity: email,
      });
      if (res?.data?.result?.senderIdentity) {
        setSenderIdentity(res.data.result.senderIdentity);
      }
      message.success(res?.data?.message || "Verification email sent.", 6);
      setEmailVerifyModalOpen(false);
      loadSenderIdentity(true);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to start email verification",
        6,
      );
    } finally {
      setVerifySubmitting(false);
    }
  };

  const resetSenderVerification = async () => {
    setSenderIdentityLoading(true);
    try {
      const res = await privateDeleteApi("/auth/sender-identities", {});
      if (res?.data?.result?.senderIdentity) {
        setSenderIdentity(res.data.result.senderIdentity);
      }
      const restoredSmtp = res?.data?.result?.smtpEmailAddress;
      if (restoredSmtp) {
        customiseEmailForm.setFieldsValue({ smtpEmailAddress: restoredSmtp });
        const nextSettings = {
          ...(emailSettingsState || {}),
          smtpEmailAddress: restoredSmtp,
        };
        setEmailSettingsState(nextSettings);
        localStorage.setItem("emailSettings", JSON.stringify(nextSettings));
      }
      await loadEmailSettings();
      message.success(res?.data?.message || "Sender verification reset.", 5);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to reset verification",
        4,
      );
    } finally {
      setSenderIdentityLoading(false);
    }
  };

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) {
      setActiveTab(tabKeyFromQuery(tab));
    }
  }, [searchParams]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    const query = TAB_KEY_TO_QUERY[key];
    if (query) {
      setSearchParams({ tab: query }, { replace: true });
    } else {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  };

  useEffect(() => {
    // Always fetch billing usage once on mount so plan info is available
    // even if the user never opens the Billing tab.
    loadBillingUsage();
    loadEmailSettings();
  }, [loadBillingUsage, loadEmailSettings]);

  useEffect(() => {
    if (isPaidPlan) {
      loadSenderIdentity(true);
    }
  }, [isPaidPlan, loadSenderIdentity]);

  useEffect(() => {
    if (!isPaidPlan || senderDisplayStatus !== "PENDING") return undefined;
    const interval = setInterval(() => {
      loadSenderIdentity(true);
    }, 20000);
    return () => clearInterval(interval);
  }, [isPaidPlan, senderDisplayStatus, loadSenderIdentity]);

  useEffect(() => {
    if (domainVerifyModalOpen && workEmailDomain) {
      setDomainVerifyValue(workEmailDomain);
    }
  }, [domainVerifyModalOpen, workEmailDomain]);

  useEffect(() => {
    if (emailVerifyModalOpen && workEmailDomain) {
      setEmailVerifyValue(`hello@${workEmailDomain}`);
    }
  }, [emailVerifyModalOpen, workEmailDomain]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData) {
      userProfileForm.setFieldsValue({
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        emailAddress: userData.emailAddress || "",
        phoneNumber: userData.phoneNumber || "",
      });
      organizationForm.setFieldsValue({
        companyName: userData.companyName || "",
        streetAddress: userData.address?.streetAddress || "",
        city: userData.address?.city || "",
        pincode: userData.address?.pincode || "",
        state: userData.address?.state || "",
        country: userData.address?.country || "",
        companyWebsite: userData.companyWebsite || "",
      });
    }
  }, [userProfileForm, organizationForm]);

  useEffect(() => {
    const userEmail = user.emailAddress || "";
    let defaultSender = "";
    if (userEmail.includes("@")) {
      const [local, domain] = userEmail.split("@");
      if (local && domain) {
        defaultSender = `${local}.${domain}@mailinflow.net`;
      }
    }
    const defaultReplyTo = userEmail || "";

    const storedFooter = emailSettingsState?.emailFooter;
    const storedWhitelabel =
      isPaidPlan && emailSettingsState?.removeWhitelabel === true;

    const footerTemplate = storedWhitelabel
      ? storedFooter
      : getDefaultBrandedFooter();

    customiseEmailForm.setFieldsValue({
      smtpEmailAddress: emailSettingsState?.smtpEmailAddress || defaultSender,
      imapEmailAddress: emailSettingsState?.imapEmailAddress || defaultReplyTo,
      clickTracking: isPaidPlan ? !!emailSettingsState?.clickTracking : false,
      openTracking: !!emailSettingsState?.openTracking,
    });
    setWhitelable(storedWhitelabel);
    if (editorRef.current) {
      editorRef.current.setContent(footerTemplate);
    }
  }, [
    customiseEmailForm,
    user.emailAddress,
    windowOrigin,
    isPaidPlan,
    emailSettingsState,
    getDefaultBrandedFooter,
  ]);

  const onFinishProfile = (values) => {
    const userData = JSON.parse(localStorage.getItem("user"));
    setLoading(true);
    dispatch(
      updateUserProfile(
        {
          firstName: values.firstName || "",
          lastName: values.lastName || "",
          emailAddress: values.emailAddress || "",
          phoneNumber: values.phoneNumber || "",
        },
        userData.id,
      ),
    )
      .then(() => {
        setLoading(false);
        const updatedUser = {
          ...userData,
          firstName: values.firstName || "",
          lastName: values.lastName || "",
          emailAddress: values.emailAddress || "",
          phoneNumber: values.phoneNumber || "",
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        message.success("Profile updated.", 6);
      })
      .catch((e) => console.log(e));
  };

  const onFinishOrganization = (values) => {
    const organizationId = getActiveOrganizationId();
    if (!organizationId) {
      message.error("No active organization. Please sign in again.");
      return;
    }

    const userData = JSON.parse(localStorage.getItem("user"));
    const address = {
      streetAddress: values.streetAddress || "",
      city: values.city || "",
      pincode: values.pincode || "",
      state: values.state || "",
      country: values.country || "",
    };

    setOrgLoading(true);
    dispatch(
      updateOrganization(organizationId, {
        name: values.companyName || "",
        website: values.companyWebsite || "",
        address,
      }),
    )
      .then((result) => {
        const org = result?.organization || {};
        const updatedUser = {
          ...userData,
          companyName: org.name ?? values.companyName ?? "",
          companyWebsite: org.website ?? values.companyWebsite ?? "",
          address: org.address ?? address,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        const organizations = JSON.parse(
          localStorage.getItem("organizations") || "[]",
        );
        const updatedOrganizations = organizations.map((orgItem) =>
          String(orgItem.id) === String(organizationId)
            ? { ...orgItem, name: updatedUser.companyName }
            : orgItem,
        );
        localStorage.setItem(
          "organizations",
          JSON.stringify(updatedOrganizations),
        );

        message.success("Organization details updated.", 6);
      })
      .catch((e) => console.log(e))
      .finally(() => setOrgLoading(false));
  };

  // PRD item 3 (FR-3.11), task 3.8. Fiduciary details used as the
  // fallback identity on every form's DPDP notice (see PUT
  // /organizations/:id/dpdp) -- loaded on mount like billing/email
  // settings above since a user may never click into this tab.
  const loadDpdpSettings = useCallback(() => {
    const organizationId = getActiveOrganizationId();
    if (!organizationId) return;
    setDpdpLoading(true);
    dispatch(fetchOrganizationDetail(organizationId))
      .then((org) => {
        dpdpForm.setFieldsValue({
          dataFiduciaryName: org?.dpdp?.dataFiduciaryName || "",
          rightsContactName: org?.dpdp?.rightsContactName || "",
          rightsContactEmail: org?.dpdp?.rightsContactEmail || "",
          boardComplaintUrl: org?.dpdp?.boardComplaintUrl || "",
        });
      })
      .catch((e) => console.log(e))
      .finally(() => setDpdpLoading(false));
  }, [dispatch, dpdpForm]);

  useEffect(() => {
    loadDpdpSettings();
  }, [loadDpdpSettings]);

  const onFinishDpdp = (values) => {
    const organizationId = getActiveOrganizationId();
    if (!organizationId) {
      message.error("No active organization. Please sign in again.");
      return;
    }
    setDpdpSaving(true);
    dispatch(updateDpdpSettings(organizationId, values))
      .then(() => {
        message.success("DPDP settings updated.", 6);
      })
      .catch((e) => {
        console.log(e);
        message.error("Failed to update DPDP settings.");
      })
      .finally(() => setDpdpSaving(false));
  };

  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);

  const onFinish2 = (values) => {
    const footerHtml = editorRef.current
      ? editorRef.current.getContent()
      : values.emailFooter;

    let effectiveFooter = footerHtml || "";

    // When whitelabel is not enabled, don't rely on the (possibly stale) editor content.
    // Always enforce the branded locked footer.
    if (!whitelabel) {
      effectiveFooter = getDefaultBrandedFooter();
    }

    if (isPaidPlan && whitelabel) {
      // Paid + whitelabel: allow fully custom footer (including removing branding)
      effectiveFooter = footerHtml || "";
    } else {
      // Free tier or paid without whitelabel: enforce presence of branding footer,
      // but still persist user edits as long as required bits remain.
      const hasBranding =
        effectiveFooter &&
        effectiveFooter.includes("/unsubscribe/") &&
        effectiveFooter.includes("/privacy-policy") &&
        effectiveFooter.includes("mceNonEditable") &&
        effectiveFooter.toLowerCase().includes("powered by") &&
        /height\s*=\s*['"]30['"]/i.test(effectiveFooter);

      if (!hasBranding) {
        // Safety net: if branding was removed, fall back to default branded footer.
        effectiveFooter = getDefaultBrandedFooter();
      }
    }

    const payload = {
      smtpEmailAddress: values.smtpEmailAddress,
      imapEmailAddress: values.imapEmailAddress,
      clickTracking: values.clickTracking || false,
      openTracking: values.openTracking || false,
      emailFooter: effectiveFooter,
      // Explicit flag — don't infer whitelabel status from footer content
      // client-side; an intentionally blank whitelabel footer must not be
      // indistinguishable from whitelabel being off.
      removeWhitelabel: isPaidPlan && whitelabel,
    };
    setEmailSettingsLoading(true);
    privatePutApi("/auth/email-settings", payload)
      .then((res) => {
        setEmailSettingsLoading(false);
        if (res?.data?.result?.emailSettings) {
          setEmailSettingsState(res.data.result.emailSettings);
          localStorage.setItem(
            "emailSettings",
            JSON.stringify(res.data.result.emailSettings),
          );
        }
        message.success("Email settings successfully updated.", 6);
      })
      .catch((error) => {
        setEmailSettingsLoading(false);
        message.error(
          error?.response?.data?.message || "Failed to update email settings",
          6,
        );
      });
  };

  const items = [
    {
      key: "profile-settings",
      label: "Profile",
      children: (
        <div style={{ margin: "1.5rem 2rem" }}>
          <Form
            form={userProfileForm}
            layout="vertical"
            onFinish={onFinishProfile}
            initialValues={{
              firstName: "",
              lastName: "",
              emailAddress: "",
              phoneNumber: "",
            }}
          >
            <Row gutter={[24, 24]}>
              <Col xs={24} md={16} lg={12}>
                <Card title="Personal details" style={{ height: "100%" }}>
                  <Form.Item
                    label="First Name"
                    name="firstName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your first name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter your first name" />
                  </Form.Item>

                  <Form.Item
                    label="Last Name"
                    name="lastName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your last name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter your last name" />
                  </Form.Item>

                  <Form.Item
                    label="Email Address"
                    name="emailAddress"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your email address",
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email address",
                      },
                    ]}
                  >
                    <Input placeholder="Enter your email address" disabled />
                  </Form.Item>

                  <Form.Item
                    label="Phone Number"
                    name="phoneNumber"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your phone number",
                      },
                    ]}
                  >
                    <Input placeholder="Enter your phone number" />
                  </Form.Item>
                </Card>
              </Col>
            </Row>

            <Row justify="end" style={{ marginTop: 24 }}>
              <Col xs={24} md={6} lg={4}>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                  >
                    Save profile
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      ),
    },
    {
      key: "security",
      label: "Security",
      children: (
        <div style={{ margin: "1.5rem 2rem" }}>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={24} md={16} lg={12}>
              <Card title="Password" style={{ height: "100%" }}>
                <Typography.Paragraph type="secondary">
                  For security, we&apos;ll send a one-time password (OTP) to{" "}
                  <strong>{user.emailAddress}</strong>. Enter that OTP along
                  with your new password to update it.
                </Typography.Paragraph>
                <Form
                  form={passwordForm}
                  layout="vertical"
                  component={false}
                  onFinish={async (values) => {
                    try {
                      const res = await privatePutApi("/auth/password", {
                        password: values.password,
                        oneTimePassword: values.oneTimePassword,
                      });
                      if (res.status === 200) {
                        message.success("Password successfully updated.", 6);
                        passwordForm.resetFields();
                        const existingUser = localStorage.getItem("user")
                          ? JSON.parse(localStorage.getItem("user"))
                          : null;
                        if (existingUser) {
                          localStorage.setItem(
                            "user",
                            JSON.stringify({
                              ...existingUser,
                              hasPassword: true,
                            }),
                          );
                          localStorage.setItem("hasPassword", "true");
                        }
                      } else {
                        message.error(
                          res?.data?.message ||
                            "Failed to update password. Please try again.",
                          6,
                        );
                      }
                    } catch (error) {
                      message.error(
                        error?.response?.data?.message ||
                          "Failed to update password. Please try again.",
                        6,
                      );
                    }
                  }}
                >
                  <Form.Item
                    label="OTP"
                    name="oneTimePassword"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the OTP sent to your email",
                      },
                    ]}
                  >
                    <Row
                      gutter={12}
                      align="middle"
                      className="settings-password-otp-row"
                    >
                      <Col flex="auto">
                        <Input.OTP
                          placeholder="Enter 6-digit OTP"
                          maxLength={6}
                          length={6}
                          size="large"
                        />
                      </Col>
                      <Col>
                        <Button
                          type="default"
                          size="large"
                          loading={passwordOtpSending}
                          disabled={passwordOtpCountdown > 0}
                          onClick={async () => {
                            try {
                              setPasswordOtpSending(true);
                              await privatePostApi("/auth/request", {
                                emailAddress: user.emailAddress,
                              });
                              startPasswordOtpCountdown();
                              message.success(
                                "OTP sent to your email address. Please check your inbox.",
                                6,
                              );
                            } catch (error) {
                              message.error(
                                error?.response?.data?.message ||
                                  "Failed to send OTP. Please try again.",
                                6,
                              );
                            } finally {
                              setPasswordOtpSending(false);
                            }
                          }}
                        >
                          {passwordOtpCountdown > 0
                            ? `Resend in ${passwordOtpCountdown}s`
                            : "Send OTP"}
                        </Button>
                      </Col>
                    </Row>
                  </Form.Item>
                  <Form.Item
                    label="New password"
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a new password",
                      },
                      {
                        min: 8,
                        message: "Password must be at least 8 characters long",
                      },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (!/[A-Za-z]/.test(value) || !/[0-9]/.test(value)) {
                            return Promise.reject(
                              new Error(
                                "Password must include at least one letter and one number",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input.Password placeholder="Enter a strong password" />
                  </Form.Item>
                  <Form.Item
                    label="Confirm password"
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your password",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Passwords do not match"),
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="Re-enter your password" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="button"
                      onClick={() => passwordForm.submit()}
                    >
                      Save password
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "notification-settings",
      label: "Notifications",
      children: "Content of Tab Pane 3",
      disabled: true,
    },
    {
      key: "organization",
      label: <span className="settings-tab-section-start">Organization</span>,
      children: (
        <div style={{ margin: "1.5rem 2rem" }}>
          {!canManageOrg() ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16, maxWidth: 720 }}
              message="Only the organization owner can edit these details."
            />
          ) : null}
          <Form
            form={organizationForm}
            layout="vertical"
            onFinish={onFinishOrganization}
            disabled={!canManageOrg()}
            initialValues={{
              companyName: "",
              streetAddress: "",
              city: "",
              pincode: "",
              state: "",
              country: "",
              companyWebsite: "",
            }}
          >
            <Row gutter={[24, 24]}>
              <Col xs={24} md={16} lg={12}>
                <Card title="Organisation details" style={{ height: "100%" }}>
                  <Form.Item
                    label="Company Name"
                    name="companyName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your company name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter your company name" />
                  </Form.Item>

                  <Form.Item
                    label="Company Address"
                    name="streetAddress"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your company address",
                      },
                    ]}
                  >
                    <Input placeholder="Enter your company address" />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="City"
                        name="city"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your city",
                          },
                        ]}
                      >
                        <Input placeholder="City" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Pincode"
                        name="pincode"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your pincode",
                          },
                        ]}
                      >
                        <Input placeholder="Pincode" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="State"
                        name="state"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your state",
                          },
                        ]}
                      >
                        <Input placeholder="State" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Country"
                        name="country"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your country",
                          },
                        ]}
                      >
                        <Input placeholder="Country" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Company Website"
                    name="companyWebsite"
                    rules={[
                      { required: false },
                      { type: "url", message: "Please enter a valid URL" },
                    ]}
                  >
                    <Input placeholder="Enter your company website" />
                  </Form.Item>
                </Card>
              </Col>
            </Row>

            {canManageOrg() ? (
              <Row justify="end" style={{ marginTop: 24 }}>
                <Col xs={24} md={6} lg={4}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      loading={orgLoading}
                    >
                      Save organization
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            ) : null}
          </Form>
        </div>
      ),
    },
    {
      key: "dpdp-compliance",
      label: "DPDP",
      children: (
        <div style={{ margin: "1.5rem 2rem" }}>
          {!canManageOrg() ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16, maxWidth: 720 }}
              message="Only the organization owner can edit these details."
            />
          ) : null}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16, maxWidth: 720 }}
            message="Fiduciary details"
            description="Used as the fallback identity, rights contact, and complaint link on every form's DPDP notice when that form's own notice doesn't set them."
          />
          <Spin spinning={dpdpLoading}>
            <Form
              form={dpdpForm}
              layout="vertical"
              onFinish={onFinishDpdp}
              disabled={!canManageOrg()}
              initialValues={{
                dataFiduciaryName: "",
                rightsContactName: "",
                rightsContactEmail: "",
                boardComplaintUrl: "",
              }}
            >
              <Row gutter={[24, 24]}>
                <Col xs={24} md={16} lg={12}>
                  <Card title="Data fiduciary" style={{ height: "100%" }}>
                    <Form.Item
                      label="Data fiduciary name"
                      name="dataFiduciaryName"
                      help="Defaults to your company name if left blank."
                    >
                      <Input placeholder="Your registered business name" />
                    </Form.Item>
                    <Form.Item
                      label="Rights-contact name"
                      name="rightsContactName"
                    >
                      <Input placeholder="Who handles data-rights requests" />
                    </Form.Item>
                    <Form.Item
                      label="Rights-contact email"
                      name="rightsContactEmail"
                      rules={[
                        { type: "email", message: "Please enter a valid email address" },
                      ]}
                    >
                      <Input placeholder="privacy@yourcompany.com" />
                    </Form.Item>
                    <Form.Item
                      label="Data Protection Board complaint URL"
                      name="boardComplaintUrl"
                      rules={[
                        { type: "url", message: "Please enter a valid URL" },
                      ]}
                    >
                      <Input placeholder="https://www.dpb.gov.in/..." />
                    </Form.Item>
                  </Card>
                </Col>
              </Row>

              {canManageOrg() ? (
                <Row justify="end" style={{ marginTop: 24 }}>
                  <Col xs={24} md={6} lg={4}>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={dpdpSaving}
                      >
                        Save DPDP settings
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              ) : null}
            </Form>
          </Spin>
        </div>
      ),
    },
    {
      key: "team",
      label: "Team",
      children: <TeamSettings />,
    },
    {
      key: "billing-and-payments",
      label: "Billing and Payments",
      children: (
        <div style={{ margin: "1.5rem 2rem" }}>
          {billingLoading ? (
            <Row>
              <Col span={24}>
                <Spin tip="Loading billing…" />
              </Col>
            </Row>
          ) : billingUsage ? (
            (() => {
              const emailPricing = billingUsage.pricing?.email || {};
              const formsPricing = billingUsage.pricing?.forms || {};
              const emailBaseVolume = emailPricing.baseVolume ?? 5000;
              const formsBaseVolume = formsPricing.baseVolume ?? 5000;
              const emailBasePrice = emailPricing.basePriceINR ?? 750;
              const emailPer1000 = emailPricing.pricePer1000INR ?? 150;
              const formsBasePrice = formsPricing.basePriceINR ?? 1050;
              const formsPer1000 = formsPricing.pricePer1000INR ?? 210;
              const emailTrialActive = billingUsage.emailTrial?.active;
              const formsTrialActive = billingUsage.formsTrial?.active;
              const formsIsPaid = billingUsage.formsPlan === "paid";
              const formatTrialEnd = (iso) => {
                if (!iso) return "";
                try {
                  return new Date(iso).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                } catch {
                  return "";
                }
              };
              return (
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%" }}
                >
                  <Typography.Title level={5} style={{ marginBottom: 0 }}>
                    Email campaigns
                  </Typography.Title>
                  <Typography.Paragraph
                    type="secondary"
                    style={{ marginBottom: 0 }}
                  >
                    Outbound email for campaigns and automation. Billed per
                    email sent each month.
                  </Typography.Paragraph>

                  <Row gutter={[24, 24]} align="stretch">
                    <Col xs={24} sm={24} md={24} lg={12}>
                      <Card
                        title="You're on this plan"
                        style={{ height: "100%" }}
                        extra={
                          billingUsage.plan === "paid" ? (
                            <Button
                              size="small"
                              loading={portalLoading}
                              onClick={async () => {
                                setPortalLoading(true);
                                try {
                                  const res = await privatePostApi(
                                    "/billing/portal",
                                    {},
                                  );
                                  if (res?.data?.result?.url) {
                                    window.open(res.data.result.url, "_blank");
                                  }
                                } catch (e) {
                                  message.error(
                                    e?.response?.data?.message ||
                                      "Failed to open portal",
                                  );
                                } finally {
                                  setPortalLoading(false);
                                }
                              }}
                            >
                              Manage subscription
                            </Button>
                          ) : null
                        }
                      >
                        <Typography.Title level={4} style={{ marginBottom: 4 }}>
                          {emailTrialActive
                            ? `Trial — ${billingUsage.emailTrial.daysRemaining} days left`
                            : billingUsage.plan === "free"
                              ? "Free"
                              : billingUsage.customEmailsPerMonth
                                ? `${billingUsage.customEmailsPerMonth.toLocaleString()} emails/month`
                                : `${emailBaseVolume.toLocaleString()} emails/month (base)`}
                        </Typography.Title>
                        <Typography.Text type="secondary">
                          {emailTrialActive
                            ? `Full base email access until ${formatTrialEnd(billingUsage.emailTrial.endsAt)}. Subscribe before the trial ends to keep sending.`
                            : billingUsage.plan === "free"
                              ? "Start free with 100 emails per month. Upgrade anytime to send more."
                              : "Monthly Mailinflow email plan. Manage or change your subscription from here."}
                        </Typography.Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={12}>
                      <Card
                        title="Credits remaining"
                        style={{ height: "100%" }}
                      >
                        <Typography.Text>
                          {billingUsage.usedThisPeriod.toLocaleString()} of{" "}
                          {billingUsage.included.toLocaleString()} emails used
                          this month
                        </Typography.Text>
                        <div
                          style={{
                            marginTop: 12,
                            height: 8,
                            background: "#1f2933",
                            borderRadius: 999,
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(
                                100,
                                (billingUsage.usedThisPeriod /
                                  Math.max(billingUsage.included, 1)) *
                                  100,
                              ).toFixed(1)}%`,
                              height: "100%",
                              borderRadius: 999,
                              background: "#30EFD3",
                              transition: "width 0.3s ease-out",
                            }}
                          />
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <Typography.Text type="secondary">
                            Monthly limit resets at the start of each calendar
                            month.
                          </Typography.Text>
                          {billingUsage.usedThisPeriod >
                            billingUsage.included && (
                            <Typography.Text
                              style={{
                                display: "block",
                                marginTop: 4,
                                color: "#ff4d4f",
                              }}
                            >
                              * Over limit. Pay balance and/or upgrade your plan
                              to continue sending.
                            </Typography.Text>
                          )}
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  {/* Email plan cards */}
                  <Row gutter={[24, 24]} align="stretch">
                    <Col xs={24} sm={24} md={12} lg={8}>
                      <Card
                        title="Free"
                        style={{ height: "100%" }}
                        extra={
                          <Typography.Text strong>
                            0 INR / month
                          </Typography.Text>
                        }
                      >
                        <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                          <li>Up to 100 outbound emails per month</li>
                          <li>
                            Unlimited contact storage for your teams and
                            audiences
                          </li>
                          <li>
                            Access to core campaign creation and sending
                            features
                          </li>
                        </ul>
                        <Button
                          disabled={billingUsage.plan === "free"}
                          loading={cancelLoading}
                          block
                          style={{ marginTop: 16 }}
                          onClick={async () => {
                            if (billingUsage.plan === "free") return;
                            setCancelLoading(true);
                            try {
                              await privatePostApi("/billing/cancel", {});
                              message.success("Switched to free plan.");
                              dispatch(
                                updateUserProfile({ ...user, plan: "free" }),
                              );
                              loadBillingUsage();
                            } catch (e) {
                              message.warning(
                                "Unable to cancel subscription. Please contact admin.",
                              );
                              setContactAdminSubject(
                                "Request to cancel subscription - Mailinflow",
                              );
                              setContactAdminMessage(
                                `I am unable to cancel my subscription from the app. Please cancel it for me.\n\nMy account email: ${user?.emailAddress || ""}`,
                              );
                              setContactAdminModalOpen(true);
                            } finally {
                              setCancelLoading(false);
                            }
                          }}
                        >
                          {billingUsage.plan === "free"
                            ? "Current plan"
                            : "Switch to free"}
                        </Button>
                      </Card>
                    </Col>

                    <Col xs={24} sm={24} md={12} lg={8}>
                      <Card
                        title={`Base — ${emailBaseVolume.toLocaleString()} emails`}
                        style={{ height: "100%" }}
                        extra={
                          <Typography.Text strong>
                            {emailBasePrice} INR / month
                          </Typography.Text>
                        }
                      >
                        <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                          <li>
                            {emailBaseVolume.toLocaleString()} outbound emails
                            per month for campaigns and automation
                          </li>
                          <li>
                            Designed for growing teams that need predictable
                            monthly capacity
                          </li>
                          <li>
                            Advanced email customisation: dedicated sender and
                            reply-to, click and open tracking, and configurable,
                            whitelabelled footer.
                          </li>
                          <li>
                            Seamless path to custom higher-volume plans as usage
                            scales
                          </li>
                        </ul>
                        <Button
                          type="primary"
                          block
                          style={{ marginTop: 16 }}
                          loading={checkoutLoading}
                          disabled={
                            billingUsage.plan === "paid" &&
                            !emailTrialActive &&
                            !billingUsage.customEmailsPerMonth
                          }
                          onClick={async () => {
                            if (
                              billingUsage.plan !== "free" &&
                              !emailTrialActive
                            )
                              return;
                            setCheckoutLoading(true);
                            try {
                              const res = await privatePostApi(
                                "/billing/checkout-session",
                                {},
                              );
                              if (res?.data?.result?.url) {
                                window.open(res.data.result.url, "_blank");
                                message.success(
                                  "Payment page opened in a new tab. Complete payment there; this page stays open.",
                                  8,
                                );
                              }
                            } catch (e) {
                              message.error(
                                e?.response?.data?.message ||
                                  "Failed to start checkout",
                              );
                            } finally {
                              setCheckoutLoading(false);
                            }
                          }}
                        >
                          {billingUsage.plan === "paid" &&
                          !emailTrialActive &&
                          !billingUsage.customEmailsPerMonth
                            ? "Current plan"
                            : "Upgrade to base plan"}
                        </Button>
                      </Card>
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={8}>
                      <Card
                        title="Custom"
                        style={{ height: "100%" }}
                        extra={
                          <Typography.Text strong>
                            {emailBasePrice} + {emailPer1000} / 1000
                          </Typography.Text>
                        }
                      >
                        <Typography.Text type="secondary">
                          Flexible, higher-volume plans tailored to your
                          organisation’s monthly email requirements above 5,000
                          messages.
                        </Typography.Text>
                        <div style={{ marginTop: 12 }}>
                          <Select
                            placeholder="Monthly emails"
                            style={{ width: "100%" }}
                            options={billingUsage.customPlanPresets?.map(
                              (p) => ({
                                label: `${p.emails.toLocaleString()} emails — ${
                                  p.monthlyAmountINR
                                } INR/month`,
                                value: p.emails,
                              }),
                            )}
                            value={customPlanEmails}
                            onChange={setCustomPlanEmails}
                          />
                        </div>
                        {customPlanEmails != null && (
                          <Typography.Text
                            type="secondary"
                            style={{ display: "block", marginTop: 8 }}
                          >
                            {billingUsage.usedThisPeriod > billingUsage.included
                              ? `Pay ${
                                  Math.ceil(
                                    (billingUsage.usedThisPeriod -
                                      billingUsage.included) /
                                      1000,
                                  ) * emailPer1000
                                } INR now for this month’s overage. `
                              : ""}
                            Your plan will be{" "}
                            {billingUsage.customPlanPresets?.find(
                              (p) => p.emails === customPlanEmails,
                            )?.monthlyAmountINR ?? 0}{" "}
                            INR/month from next month.
                          </Typography.Text>
                        )}
                        <Button
                          type="primary"
                          block
                          style={{ marginTop: 12 }}
                          disabled={
                            !customPlanEmails || billingUsage.plan !== "paid"
                          }
                          loading={customPlanLoading}
                          onClick={async () => {
                            if (
                              !customPlanEmails ||
                              billingUsage.plan !== "paid"
                            )
                              return;
                            setCustomPlanLoading(true);
                            try {
                              const res = await privatePostApi(
                                "/billing/custom-plan",
                                {
                                  monthlyEmails: customPlanEmails,
                                },
                              );
                              if (res?.data?.result?.url) {
                                window.open(res.data.result.url, "_blank");
                                message.success(
                                  "Opened in a new tab. Complete payment there if needed; this page stays open.",
                                  6,
                                );
                              } else {
                                message.success("Plan updated");
                                loadBillingUsage();
                                setCustomPlanEmails(null);
                              }
                            } catch (e) {
                              message.error(
                                e?.response?.data?.message ||
                                  "Failed to update plan",
                              );
                            } finally {
                              setCustomPlanLoading(false);
                            }
                          }}
                        >
                          {billingUsage.plan === "paid"
                            ? "Confirm and pay"
                            : "Upgrade to paid first"}
                        </Button>
                      </Card>
                    </Col>
                  </Row>

                  <Typography.Title level={5} style={{ marginTop: 8 }}>
                    Forms
                  </Typography.Title>
                  <Typography.Paragraph
                    type="secondary"
                    style={{ marginBottom: 0 }}
                  >
                    Hosted forms and submissions. Separate from email campaigns.
                    Billed per form submission received each month.
                  </Typography.Paragraph>

                  <Row gutter={[24, 24]} align="stretch">
                    <Col xs={24} sm={24} md={24} lg={12}>
                      <Card
                        title="You're on this plan"
                        style={{ height: "100%" }}
                        extra={
                          formsIsPaid ? (
                            <Button
                              size="small"
                              loading={portalLoading}
                              onClick={async () => {
                                setPortalLoading(true);
                                try {
                                  const res = await privatePostApi(
                                    "/billing/portal",
                                    {},
                                  );
                                  if (res?.data?.result?.url) {
                                    window.open(res.data.result.url, "_blank");
                                  }
                                } catch (e) {
                                  message.error(
                                    e?.response?.data?.message ||
                                      "Failed to open portal",
                                  );
                                } finally {
                                  setPortalLoading(false);
                                }
                              }}
                            >
                              Manage subscription
                            </Button>
                          ) : null
                        }
                      >
                        <Typography.Title level={4} style={{ marginBottom: 4 }}>
                          {formsTrialActive
                            ? `Trial — ${billingUsage.formsTrial.daysRemaining} days left`
                            : !formsIsPaid
                              ? "Free"
                              : billingUsage.customFormSubmissionsPerMonth
                                ? `${billingUsage.customFormSubmissionsPerMonth.toLocaleString()} submissions/month`
                                : `${formsBaseVolume.toLocaleString()} submissions/month (base)`}
                        </Typography.Title>
                        <Typography.Text type="secondary">
                          {formsTrialActive
                            ? `Full base Forms access until ${formatTrialEnd(billingUsage.formsTrial.endsAt)}. Subscribe before the trial ends to keep paid limits.`
                            : !formsIsPaid
                              ? "Up to 2 forms and 100 submissions per month. Upgrade for higher volume and retention."
                              : "Monthly Forms plan. Manage or change your Forms subscription from here."}
                        </Typography.Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={12}>
                      <Card
                        title="Credits remaining"
                        style={{ height: "100%" }}
                      >
                        <Typography.Text>
                          {(
                            billingUsage.submissionsThisMonth ?? 0
                          ).toLocaleString()}{" "}
                          of{" "}
                          {(
                            billingUsage.formsIncluded ??
                            billingUsage.limits?.submissionsPerMonth ??
                            100
                          ).toLocaleString()}{" "}
                          submissions used this month
                        </Typography.Text>
                        <div
                          style={{
                            marginTop: 12,
                            height: 8,
                            background: "#1f2933",
                            borderRadius: 999,
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(
                                100,
                                ((billingUsage.submissionsThisMonth ?? 0) /
                                  Math.max(
                                    billingUsage.formsIncluded ??
                                      billingUsage.limits
                                        ?.submissionsPerMonth ??
                                      100,
                                    1,
                                  )) *
                                  100,
                              ).toFixed(1)}%`,
                              height: "100%",
                              borderRadius: 999,
                              background: "#30EFD3",
                              transition: "width 0.3s ease-out",
                            }}
                          />
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <Typography.Text type="secondary">
                            Monthly limit resets at the start of each calendar
                            month.
                          </Typography.Text>
                          {formsIsPaid &&
                            (billingUsage.submissionsThisMonth ?? 0) >
                              (billingUsage.formsIncluded ?? 0) && (
                              <Typography.Text
                                style={{
                                  display: "block",
                                  marginTop: 4,
                                  color: "#ff4d4f",
                                }}
                              >
                                * Over Forms limit. Pay balance and/or upgrade
                                your Forms plan.
                              </Typography.Text>
                            )}
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[24, 24]} align="stretch">
                    <Col xs={24} sm={24} md={12} lg={8}>
                      <Card
                        title="Forms — Free"
                        style={{ height: "100%" }}
                        extra={
                          <Typography.Text strong>
                            0 INR / month
                          </Typography.Text>
                        }
                      >
                        <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                          <li>Up to 2 forms</li>
                          <li>100 submissions per month</li>
                          <li>30-day submission retention</li>
                        </ul>
                        <Button
                          disabled={!formsIsPaid}
                          loading={formsCancelLoading}
                          block
                          style={{ marginTop: 16 }}
                          onClick={async () => {
                            if (!formsIsPaid) return;
                            setFormsCancelLoading(true);
                            try {
                              await privatePostApi("/billing/forms/cancel", {});
                              message.success("Switched to free Forms plan.");
                              loadBillingUsage();
                            } catch (e) {
                              message.warning(
                                "Unable to cancel Forms subscription. Please contact admin.",
                              );
                              setContactAdminSubject(
                                "Request to cancel Forms subscription - Mailinflow",
                              );
                              setContactAdminMessage(
                                `I am unable to cancel my Forms subscription from the app. Please cancel it for me.\n\nMy account email: ${user?.emailAddress || ""}`,
                              );
                              setContactAdminModalOpen(true);
                            } finally {
                              setFormsCancelLoading(false);
                            }
                          }}
                        >
                          {!formsIsPaid ? "Current plan" : "Switch to free"}
                        </Button>
                      </Card>
                    </Col>

                    <Col xs={24} sm={24} md={12} lg={8}>
                      <Card
                        title={`Forms — ${formsBaseVolume.toLocaleString()} submissions`}
                        style={{ height: "100%" }}
                        extra={
                          <Typography.Text strong>
                            {formsBasePrice} INR / month
                          </Typography.Text>
                        }
                      >
                        <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                          <li>
                            {formsBaseVolume.toLocaleString()} submissions per
                            month
                          </li>
                          <li>Unlimited forms</li>
                          <li>365-day submission retention</li>
                          <li>Webhooks, workflows, and spam protection</li>
                        </ul>
                        <Button
                          type="primary"
                          block
                          style={{ marginTop: 16 }}
                          loading={formsCheckoutLoading}
                          disabled={
                            formsIsPaid &&
                            !formsTrialActive &&
                            !billingUsage.customFormSubmissionsPerMonth
                          }
                          onClick={async () => {
                            if (formsIsPaid) return;
                            setFormsCheckoutLoading(true);
                            try {
                              const res = await privatePostApi(
                                "/billing/forms/checkout-session",
                                {},
                              );
                              if (res?.data?.result?.url) {
                                window.open(res.data.result.url, "_blank");
                                message.success(
                                  "Forms payment page opened in a new tab.",
                                  8,
                                );
                              }
                            } catch (e) {
                              message.error(
                                e?.response?.data?.message ||
                                  "Failed to start Forms checkout",
                              );
                            } finally {
                              setFormsCheckoutLoading(false);
                            }
                          }}
                        >
                          {formsIsPaid &&
                          !formsTrialActive &&
                          !billingUsage.customFormSubmissionsPerMonth
                            ? "Current plan"
                            : "Upgrade Forms plan"}
                        </Button>
                      </Card>
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={8}>
                      <Card
                        title="Forms — Custom volume"
                        style={{ height: "100%" }}
                        extra={
                          <Typography.Text strong>
                            {formsBasePrice} + {formsPer1000} / 1000
                          </Typography.Text>
                        }
                      >
                        <Typography.Text type="secondary">
                          Higher monthly submission limits above{" "}
                          {formsBaseVolume.toLocaleString()}.
                        </Typography.Text>
                        <div style={{ marginTop: 12 }}>
                          <Select
                            placeholder="Monthly submissions"
                            style={{ width: "100%" }}
                            options={billingUsage.formCustomPlanPresets?.map(
                              (p) => ({
                                label: `${p.submissions.toLocaleString()} submissions — ${p.monthlyAmountINR} INR/month`,
                                value: p.submissions,
                              }),
                            )}
                            value={customFormSubmissions}
                            onChange={setCustomFormSubmissions}
                          />
                        </div>
                        {customFormSubmissions != null && (
                          <Typography.Text
                            type="secondary"
                            style={{ display: "block", marginTop: 8 }}
                          >
                            {(billingUsage.submissionsThisMonth ?? 0) >
                            (billingUsage.formsIncluded ?? 0)
                              ? `Pay ${
                                  Math.ceil(
                                    ((billingUsage.submissionsThisMonth ?? 0) -
                                      (billingUsage.formsIncluded ?? 0)) /
                                      1000,
                                  ) * formsPer1000
                                } INR now for this month’s overage. `
                              : ""}
                            Your plan will be{" "}
                            {billingUsage.formCustomPlanPresets?.find(
                              (p) => p.submissions === customFormSubmissions,
                            )?.monthlyAmountINR ?? 0}{" "}
                            INR/month from next month.
                          </Typography.Text>
                        )}
                        <Button
                          type="primary"
                          block
                          style={{ marginTop: 12 }}
                          disabled={!customFormSubmissions || !formsIsPaid}
                          loading={formsCustomPlanLoading}
                          onClick={async () => {
                            if (!customFormSubmissions || !formsIsPaid) return;
                            setFormsCustomPlanLoading(true);
                            try {
                              const res = await privatePostApi(
                                "/billing/forms/custom-plan",
                                {
                                  monthlySubmissions: customFormSubmissions,
                                },
                              );
                              if (res?.data?.result?.url) {
                                window.open(res.data.result.url, "_blank");
                                message.success(
                                  "Opened in a new tab. Complete payment there if needed.",
                                  6,
                                );
                              } else {
                                message.success("Forms plan updated");
                                loadBillingUsage();
                                setCustomFormSubmissions(null);
                              }
                            } catch (e) {
                              message.error(
                                e?.response?.data?.message ||
                                  "Failed to update Forms plan",
                              );
                            } finally {
                              setFormsCustomPlanLoading(false);
                            }
                          }}
                        >
                          {formsIsPaid
                            ? "Confirm and pay"
                            : "Upgrade Forms plan first"}
                        </Button>
                      </Card>
                    </Col>
                  </Row>
                </Space>
              );
            })()
          ) : (
            <Row>
              <Col span={24}>
                <Typography.Text type="secondary">
                  Unable to load billing. Try again later.
                </Typography.Text>
              </Col>
            </Row>
          )}
        </div>
      ),
    },
    {
      key: "email-settings",
      label: "Customizations",
      children: (
        <div style={{ margin: "1.5rem 2rem" }}>
          <Form
            form={customiseEmailForm}
            layout="vertical"
            onFinish={onFinish2}
            initialValues={{
              smtpEmailAddress: "",
              imapEmailAddress: "",
              clickTracking: false,
              openTracking: false,
            }}
          >
            <Row gutter={[24, 24]}>
              {isPaidPlan && (
                <Col xs={24}>
                  <Card
                    title="Sender authentication"
                    extra={senderStatusTag(senderDisplayStatus)}
                  >
                    <Spin spinning={senderIdentityLoading}>
                      <Typography.Paragraph type="secondary">
                        Trial sending uses{" "}
                        <Typography.Text code>@mailinflow.net</Typography.Text>{" "}
                        (no verification needed). To send from your company
                        domain, verify the domain or a single email address on{" "}
                        <Typography.Text code>
                          @{workEmailDomain || "your-domain.com"}
                        </Typography.Text>
                        .
                      </Typography.Paragraph>

                      {isSenderFullyVerified && (
                        <Alert
                          type="success"
                          showIcon
                          style={{ marginBottom: 16 }}
                          message={`Verified: ${senderIdentity.identity}`}
                          description={
                            senderIdentity.needsSenderUpdate ||
                            usesTrialSender ? (
                              <Space direction="vertical" size="small">
                                <span>
                                  Campaigns still use your trial sender (
                                  <Typography.Text code>
                                    @mailinflow.net
                                  </Typography.Text>
                                  ). Set your company sender to start sending
                                  from{" "}
                                  <Typography.Text strong>
                                    {suggestedSenderEmail}
                                  </Typography.Text>
                                  .
                                </span>
                                <Button
                                  type="primary"
                                  size="small"
                                  loading={applySenderLoading}
                                  onClick={() =>
                                    applySenderFromVerification(
                                      suggestedSenderEmail,
                                    )
                                  }
                                >
                                  Use verified sender
                                </Button>
                              </Space>
                            ) : (
                              <span>
                                Sender email is set to{" "}
                                <Typography.Text strong>
                                  {smtpFormValue ||
                                    emailSettingsState?.smtpEmailAddress}
                                </Typography.Text>
                                . Campaigns will use this address.
                              </span>
                            )
                          }
                        />
                      )}

                      {!isSenderFullyVerified &&
                        senderIdentity?.type === "email" &&
                        senderIdentity?.identity && (
                          <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={`Check the inbox for ${senderIdentity.identity}`}
                            description="Open the AWS verification email and click the link, then use Check status."
                          />
                        )}

                      {!isSenderFullyVerified &&
                        senderIdentity?.type === "domain" &&
                        senderIdentity?.verificationRecords?.length > 0 && (
                          <>
                            <Typography.Paragraph>
                              Add these DNS records at your domain registrar,
                              then check status (propagation can take up to 72
                              hours). The DKIM and MAIL FROM (MX + SPF) rows
                              are required to verify; the DMARC row is
                              recommended.
                            </Typography.Paragraph>
                            <Alert
                              type="info"
                              showIcon
                              style={{ marginBottom: 16 }}
                              message="Gmail, Yahoo and Outlook require SPF, DKIM and a DMARC record (at least p=none) once you send more than 5,000 emails a day."
                            />
                            <Table
                              size="small"
                              pagination={false}
                              rowKey={(row) => `${row.type}-${row.name}`}
                              dataSource={senderIdentity.verificationRecords}
                              columns={[
                                { title: "Type", dataIndex: "type", width: 80 },
                                {
                                  title: "Name / Host",
                                  dataIndex: "name",
                                  render: (name) => (
                                    <Typography.Text
                                      copyable={{ text: name }}
                                      style={{ wordBreak: "break-all" }}
                                    >
                                      {name}
                                    </Typography.Text>
                                  ),
                                },
                                {
                                  title: "Value",
                                  dataIndex: "value",
                                  render: (value, row) => (
                                    <Space>
                                      <Typography.Text
                                        style={{ wordBreak: "break-all" }}
                                      >
                                        {row.priority
                                          ? `${row.priority} ${value}`
                                          : value}
                                      </Typography.Text>
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          copyDnsValue(
                                            row.priority
                                              ? `${row.priority} ${value}`
                                              : value,
                                          )
                                        }
                                      >
                                        Copy
                                      </Button>
                                    </Space>
                                  ),
                                },
                                {
                                  title: "Status",
                                  key: "status",
                                  width: 130,
                                  render: (_, row) =>
                                    row.recommended ? (
                                      <Tag
                                        color={
                                          senderIdentity.dmarcRecord?.published
                                            ? "green"
                                            : "gold"
                                        }
                                      >
                                        Recommended
                                        {senderIdentity.dmarcRecord?.published
                                          ? " · published"
                                          : ""}
                                      </Tag>
                                    ) : (
                                      <Tag color="blue">Required</Tag>
                                    ),
                                },
                              ]}
                              style={{ marginBottom: 16 }}
                            />
                            {senderIdentity.mailFrom?.domain && (
                              <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
                                MAIL FROM ({senderIdentity.mailFrom.domain}):{" "}
                                {senderIdentity.mailFrom.status}
                              </Typography.Paragraph>
                            )}
                          </>
                        )}

                      <Space wrap>
                        <Button
                          type="primary"
                          onClick={() => setDomainVerifyModalOpen(true)}
                        >
                          Verify domain
                        </Button>
                        <Button onClick={() => setEmailVerifyModalOpen(true)}>
                          Verify email address
                        </Button>
                        <Button
                          onClick={() => loadSenderIdentity(false)}
                          loading={senderIdentityLoading}
                        >
                          Check status
                        </Button>
                        {senderIdentity?.identity && (
                          <Button danger onClick={resetSenderVerification}>
                            Reset verification
                          </Button>
                        )}
                      </Space>
                    </Spin>
                  </Card>
                </Col>
              )}

              <Col xs={24} sm={24} md={24} lg={12}>
                <Card title="Delivery settings" style={{ height: "100%" }}>
                  <Form.Item
                    label={
                      <span>
                        Sender Email Address{" "}
                        {!isPaidPlan && (
                          <Typography.Text
                            type="secondary"
                            style={{ marginLeft: 8 }}
                          >
                            (Upgrade)
                          </Typography.Text>
                        )}
                      </span>
                    }
                    name="smtpEmailAddress"
                    extra={
                      isPaidPlan ? (
                        <Typography.Text type="secondary">
                          Use @mailinflow.net for trial sending, or your
                          verified company domain after authentication above.
                        </Typography.Text>
                      ) : null
                    }
                    rules={[
                      {
                        required: true,
                        message: "Please enter your sender email address",
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email address",
                      },
                    ]}
                  >
                    <Input
                      placeholder="name.domain@mailinflow.net"
                      disabled={senderInputDisabled}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span>
                        Reply-To{" "}
                        {!isPaidPlan && (
                          <Typography.Text
                            type="secondary"
                            style={{ marginLeft: 8 }}
                          >
                            (Upgrade)
                          </Typography.Text>
                        )}
                      </span>
                    }
                    name="imapEmailAddress"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your reply-to email address",
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email address",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Replies will go to this address"
                      disabled={!isPaidPlan}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span>
                        Click Tracking{" "}
                        {!isPaidPlan && (
                          <Typography.Text
                            type="secondary"
                            style={{ marginLeft: 8 }}
                          >
                            (Upgrade)
                          </Typography.Text>
                        )}
                      </span>
                    }
                    name="clickTracking"
                    valuePropName="checked"
                  >
                    <Checkbox disabled={!isPaidPlan}>
                      Enable Click Tracking
                    </Checkbox>
                  </Form.Item>

                  <Form.Item
                    label="Open Tracking"
                    name="openTracking"
                    valuePropName="checked"
                  >
                    <Checkbox>Enable Open Tracking</Checkbox>
                  </Form.Item>
                </Card>
              </Col>

              <Col xs={24} sm={24} md={24} lg={12}>
                <Card
                  title="Email footer & branding"
                  style={{ height: "100%" }}
                >
                  <Form.Item label="Email Footer">
                    <Checkbox
                      checked={whitelabel}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setWhitelable(checked);
                        if (editorRef.current) {
                          if (checked) {
                            // User manages the entire footer themselves
                            editorRef.current.setContent(
                              "<div style='max-width: 600px; margin: auto;'><div class='mceEditable'></div></div>",
                            );
                          } else {
                            // Restore default footer with unsubscribe, privacy, and branding
                            editorRef.current.setContent(
                              getDefaultBrandedFooter(),
                            );
                          }
                        }
                      }}
                      disabled={!isPaidPlan}
                    >
                      Remove Whitelabel
                      {!isPaidPlan && (
                        <Typography.Text
                          type="secondary"
                          style={{ marginLeft: 8 }}
                        >
                          (Upgrade)
                        </Typography.Text>
                      )}
                    </Checkbox>
                  </Form.Item>

                  {whitelabel ? (
                    <Editor
                      key="whitelabel-editor"
                      tinymceScriptSrc={"/tinymce/tinymce.min.js"}
                      onInit={(evt, editor) => {
                        editorRef.current = editor;
                      }}
                      initialValue={
                        emailSettingsState?.emailFooter ||
                        getEmptyWhitelabelFooter()
                      }
                      init={{
                        height: "calc(100vh - 360px)",
                        content_style: `
                          body.mce-content-body {
                            background-color: #f9fafb !important;
                            color: #111827 !important;
                            font-family: "Lekton", serif;
                            padding: 16px;
                          }
                          body.mce-content-body * {
                            color: #111827 !important;
                            font-family: "Lekton", serif;
                            font-size: 13px;
                            line-height: 1.5;
                          }
                          body.mce-content-body a {
                            text-decoration: underline;
                          }
                        `,
                        menubar: false,
                        selector: "textarea",
                        convert_urls: true,
                        relative_urls: false,
                        remove_script_host: false,
                        image_title: true,
                        automatic_uploads: true,
                        file_picker_types: "image",
                        file_picker_callback: (cb, value, meta) => {
                          const input = document.createElement("input");
                          input.setAttribute("type", "file");
                          input.setAttribute("accept", "image/*");

                          input.addEventListener("change", (e) => {
                            const file = e.target.files[0];

                            const reader = new FileReader();
                            reader.addEventListener("load", () => {
                              const id = "blobid" + new Date().getTime();
                              const blobCache =
                                tinymce.activeEditor.editorUpload.blobCache;
                              const base64 = reader.result.split(",")[1];
                              const blobInfo = blobCache.create(
                                id,
                                file,
                                base64,
                              );
                              blobCache.add(blobInfo);

                              cb(blobInfo.blobUri(), { title: file.name });
                            });
                            reader.readAsDataURL(file);
                          });

                          input.click();
                        },
                        plugins: [
                          "advlist",
                          "autolink",
                          "lists",
                          "link",
                          "image",
                          "charmap",
                          "preview",
                          "insertdatetime",
                          "anchor",
                          "searchreplace",
                          "visualblocks",
                          "code",
                          "media",
                          "table",
                          "wordcount",
                          "quickbars",
                          "emoticons",
                        ],
                        toolbar: [
                          "undo redo | fontfamily fontsize | bold italic underline | forecolor backcolor",
                          "alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | charmap emoticons link | searchreplace preview",
                        ],
                        quickbars_insert_toolbar:
                          "image media table insertdatetime",
                        quickbars_selection_toolbar:
                          "bold italic underline strikethrough | code blockquote quicklink removeformat",
                      }}
                    />
                  ) : null}
                </Card>
              </Col>
            </Row>

            <Row justify="end" style={{ marginTop: 24 }}>
              <Col xs={24} md={6} lg={4}>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={emailSettingsLoading}
                  >
                    Save Settings
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      ),
    },
    {
      key: "deliverability",
      label: "Deliverability",
      children: <DeliverabilitySettings />,
    },
    {
      key: "contact-support",
      label: "Contact support",
      children: (
        <div style={{ margin: "1.5rem 2rem" }}>
          <Card title="Contact support" style={{ maxWidth: 640 }}>
            <Typography.Paragraph type="secondary">
              Send a message to the administrator. We'll get back to you as soon
              as we can.
            </Typography.Paragraph>
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <div>
                <Typography.Text strong>Subject</Typography.Text>
                <Input
                  value={supportSubject}
                  onChange={(e) => setSupportSubject(e.target.value)}
                  placeholder="e.g. Billing question, Technical issue"
                  style={{ marginTop: 8 }}
                />
              </div>
              <div>
                <Typography.Text strong>Message</Typography.Text>
                <TextArea
                  value={supportMessage}
                  onChange={(e) => setSupportMessage(e.target.value)}
                  placeholder="Describe your question or issue..."
                  rows={5}
                  style={{ marginTop: 8 }}
                />
              </div>
              <Button
                type="primary"
                loading={supportSending}
                onClick={async () => {
                  const sub = (supportSubject || "").trim();
                  const msg = (supportMessage || "").trim();
                  if (!sub || !msg) {
                    message.warning("Please enter both subject and message.");
                    return;
                  }
                  setSupportSending(true);
                  try {
                    await privatePostApi("/billing/contact-admin", {
                      subject: sub,
                      message: msg,
                    });
                    message.success(
                      "Message sent. We'll get back to you soon.",
                    );
                    setSupportSubject("");
                    setSupportMessage("");
                  } catch (e) {
                    // Error toast handled by apiHelper
                  } finally {
                    setSupportSending(false);
                  }
                }}
              >
                Send message
              </Button>
            </Space>
          </Card>
        </div>
      ),
    },
  ];

  const filteredItems = items.filter((tab) => {
    if (tab.key === "billing-and-payments" && !canManageBilling()) {
      return false;
    }
    if (tab.key === "email-settings" && !canManageEmailSettings()) {
      return false;
    }
    if (tab.key === "deliverability" && !canManageEmailSettings()) {
      return false;
    }
    return true;
  });

  return (
    <div className="Settings">
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={filteredItems}
      />
      <Modal
        title="Set your sender email"
        open={senderUpdateModalOpen}
        onCancel={() => setSenderUpdateModalOpen(false)}
        footer={[
          <Button key="later" onClick={() => setSenderUpdateModalOpen(false)}>
            I'll do it later
          </Button>,
          <Button
            key="apply"
            type="primary"
            loading={applySenderLoading}
            onClick={() => applySenderFromVerification(suggestedSenderEmail)}
          >
            Use {suggestedSenderEmail}
          </Button>,
        ]}
      >
        <Typography.Paragraph>
          Your domain or email is verified. To send campaigns from your company
          (not the trial <Typography.Text code>@mailinflow.net</Typography.Text>{" "}
          address), confirm this sender:
        </Typography.Paragraph>
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          {suggestedSenderEmail}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          You can change it anytime under Delivery settings below.
        </Typography.Paragraph>
      </Modal>

      <Modal
        title="Verify domain"
        open={domainVerifyModalOpen}
        onCancel={() => setDomainVerifyModalOpen(false)}
        onOk={startDomainVerification}
        confirmLoading={verifySubmitting}
        okText="Start verification"
      >
        <Typography.Paragraph type="secondary">
          We will register your domain with AWS SES and show DKIM DNS records
          to add at your registrar. You can verify any domain you own — it
          doesn&apos;t need to match your account email.
        </Typography.Paragraph>
        <Typography.Text strong>Domain</Typography.Text>
        <Input
          value={domainVerifyValue}
          onChange={(e) => setDomainVerifyValue(e.target.value)}
          placeholder={workEmailDomain || "company.com"}
          style={{ marginTop: 8 }}
        />
      </Modal>

      <Modal
        title="Verify email address"
        open={emailVerifyModalOpen}
        onCancel={() => setEmailVerifyModalOpen(false)}
        onOk={startEmailVerification}
        confirmLoading={verifySubmitting}
        okText="Send verification email"
      >
        <Typography.Paragraph type="secondary">
          AWS will send a verification link to this address. You can verify
          any email address you can access — it doesn&apos;t need to match
          your account email.
        </Typography.Paragraph>
        <Typography.Text strong>Email address</Typography.Text>
        <Input
          type="email"
          value={emailVerifyValue}
          onChange={(e) => setEmailVerifyValue(e.target.value)}
          placeholder={
            workEmailDomain ? `hello@${workEmailDomain}` : "hello@company.com"
          }
          style={{ marginTop: 8 }}
        />
      </Modal>

      <Modal
        title="Contact admin"
        open={contactAdminModalOpen}
        onCancel={() => setContactAdminModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setContactAdminModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={contactAdminSending}
            onClick={async () => {
              if (!contactAdminSubject.trim() || !contactAdminMessage.trim()) {
                message.error("Please fill subject and message.");
                return;
              }
              setContactAdminSending(true);
              try {
                await privatePostApi("/billing/contact-admin", {
                  subject: contactAdminSubject,
                  message: contactAdminMessage,
                });
                message.success("Message sent. Admin will get back to you.");
                setContactAdminModalOpen(false);
              } catch (e) {
                message.error(
                  e?.response?.data?.message || "Failed to send message",
                );
              } finally {
                setContactAdminSending(false);
              }
            }}
          >
            Send
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Typography.Text strong>Subject</Typography.Text>
            <Input
              value={contactAdminSubject}
              onChange={(e) => setContactAdminSubject(e.target.value)}
              placeholder="Subject"
              style={{ marginTop: 8 }}
            />
          </div>
          <div>
            <Typography.Text strong>Message</Typography.Text>
            <TextArea
              value={contactAdminMessage}
              onChange={(e) => setContactAdminMessage(e.target.value)}
              placeholder="Your message"
              rows={5}
              style={{ marginTop: 8 }}
            />
          </div>
          <Typography.Text type="secondary">
            This will send an email to the administrator. They can cancel your
            subscription for you.
          </Typography.Text>
        </Space>
      </Modal>
    </div>
  );
}
